/**
 * The WORKINGS behind a pricing quote — everything pricing's own quote builder shows, drawn in
 * sales (Callum, 2026-09-25: "fully featured… shows all the same rich workings and analysis as
 * it does in the pricing app").
 *
 * 🔴 Nothing here is computed. Pricing's engine returns every line's `rationale` (basis, all
 * the signals, strategy and condition multipliers, flags, surge, win-back) and the quote's
 * context (strategy + why it was swapped, customer features, conditions that fired); this
 * module only lays them out. Competitors come from pricing's `/market` via our
 * `/partmarket` — the desk's own comparison.
 *
 *   quoteContext(q, account)     the banner above the lines
 *   marginCell(pct)              pricing's colours: thin < 15% red, fat > 45% amber
 *   confidenceBadge(c)
 *   workingsHtml(line, ccy)      the expanded "how this price was worked out"
 *   loadCompetitors(box, …)      the market for one part, on demand
 */
import { escapeHtml as esc, getIcon } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { money, shortDate } from '../lib/fig.js';

const BASIS = {
  own_last: 'what they last paid', quoted_last: 'what we last quoted them', prev_quote: 'their previous quote',
  cohort: 'what similar customers pay', vendor_landscape: 'competitor prices', cost_plus: 'our cost plus the strategy margin',
  price_book: 'their own price book — the agreed price',
};
const FLAG = {
  no_data: ['red', 'No price data — set a price'], margin_floor_raised: ['amber', 'Raised to the margin floor'],
  margin_max_capped: ['amber', 'Capped at the maximum margin'], above_market: ['amber', 'Above the market'],
  winback_capped: ['blue', 'Capped at the previous quote (win-back)'],
  price_book_overridden_by_winback: ['blue', 'Win-back: priced instead of their price book'],
  price_book_suspect_unit: ['red', 'Price book price looks like a unit error — not used'],
  price_book_below_cost: ['red', 'Agreed book price is below cost'],
  price_book_below_margin_floor: ['amber', 'Agreed book price is under the margin floor'],
  below_book_first_break: ['amber', "Below the book's first break — its first price used"],
};
const CONF = { high: 'green', medium: 'blue', low: 'amber', none: 'gray' };

export function marginCell(m, was) {
  if (m == null || Number.isNaN(m)) return '<span class="sc-list-meta" title="No buy cost on record">—</span>';
  let color = 'var(--text-secondary)', tip = '';
  if (m < 0) { color = 'var(--red-600,#dc2626)'; tip = 'below cost'; }
  else if (m < 15) { color = 'var(--red-600,#dc2626)'; tip = 'thin (<15%)'; }
  else if (m > 45) { color = 'var(--yellow-700,#a16207)'; tip = 'fat (>45%)'; }
  const under = was != null && Math.abs(m - was) >= 0.05
    ? `<div style="font-size:var(--text-xs);color:var(--text-tertiary);white-space:nowrap">suggested ${was.toFixed(1)}% ${m > was
      ? '<span style="color:var(--green-600,#059669)">▲</span>' : '<span style="color:var(--red-500,#ef4444)">▼</span>'}</div>` : '';
  return `<div title="${esc(tip)}"><span style="font-variant-numeric:tabular-nums;font-weight:${m < 0 ? 700 : 500};color:${color}">${m.toFixed(1)}%</span>${under}</div>`;
}

export function confidenceBadge(c) {
  if (!c) return '';
  return `<span class="badge badge-${CONF[c] || 'gray'}" title="How much evidence the price stands on">${esc(c)}</span>`;
}

function fmtMoney(ccy) {
  const f = money(ccy, 2);
  return (v) => (v == null ? '—' : (Math.abs(v) < 1 ? money(ccy, 4)(v) : f(v)));
}

/** The banner above the priced lines: strategy (and why it changed), the customer, what fired. */
export function quoteContext(q) {
  const m = fmtMoney(q.currency);
  const f = q.features || {};
  const chips = [
    f.tier && `Tier ${esc(f.tier)}`,
    f.annual_spend != null && `${esc(m(f.annual_spend))} spent in 12 months`,
    f.order_count != null && `${f.order_count} orders`,
    f.win_rate != null && `wins ${Math.round(f.win_rate * 100)}% of quotes (${f.won_count} of ${f.quote_count})`,
    f.aov != null && `average order ${esc(m(f.aov))}`,
    f.last_order_days != null && `last ordered ${f.last_order_days} days ago`,
    f.sector && esc(f.sector),
    f.country && esc(f.country),
  ].filter(Boolean);
  const note = q.strategy_note;
  const prevq = q.previous_quote;
  return `<div class="card card-body" style="margin-bottom:var(--space-3);font-size:var(--text-sm)">
    <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;align-items:center">
      <strong>Strategy:</strong> <span class="badge badge-blue">${esc((note && note.used_name) || q.strategy || 'default')}</span>
      ${q.priced_count != null ? `<span class="sc-list-meta">${q.priced_count} of ${q.line_count} lines priced</span>` : ''}

      ${q.surge_enabled === false ? '<span class="sc-list-meta" title="Surge pricing is switched off for this Company">surge off</span>'
        : q.surge_applied ? `<span class="badge badge-amber">surge applied to ${q.surge_applied} line${q.surge_applied === 1 ? '' : 's'}</span>`
        : q.surge_available_count ? `<span class="sc-list-meta">${q.surge_available_count} line${q.surge_available_count === 1 ? ' could' : 's could'} take a surge</span>` : ''}
    </div>
    ${note && note.reason ? `<div style="margin-top:4px;color:var(--amber-700,#b45309)">${esc(note.requested_name || note.requested || 'The strategy you picked')}
      does not cover this customer (${esc(note.reason)}) — priced on <strong>${esc(note.used_name || note.used)}</strong> instead.</div>` : ''}
    ${chips.length ? `<div class="sc-list-meta" style="margin-top:4px">${chips.join(' · ')}</div>` : ''}
    ${(q.conditions_fired || []).length ? `<div style="margin-top:4px"><span class="sc-list-meta">Rules that fired for this customer:</span>
      ${q.conditions_fired.map(c => `<span class="badge badge-gray" title="${esc(JSON.stringify(c.then || {}))}">${esc(typeof c.when === 'string' ? c.when : JSON.stringify(c.when))}</span>`).join(' ')}</div>` : ''}
    ${prevq ? `<div style="margin-top:4px">Win-back of <strong>${esc(prevq.quote_no)}</strong>${prevq.date ? ` (${esc(shortDate(prevq.date))})` : ''} —
      ${esc(fmtMoney(prevq.currency || q.currency)(prevq.total_value))} then; ${prevq.matched_lines != null ? `${prevq.matched_lines} lines matched` : ''}.</div>` : ''}
  </div>
  ${bookBanner(q.lines, (note && note.used_name) || q.strategy)}`;
}

// ── The customer's own price book (pricing DO #86, Callum 2026-09-28) ─────────────
// "user should be able to select other quote strategies but there needs to be a really
// clear UX to show that xyz items are in the price book and have been priced in accordance
// with it". A part in their book is quoted at the AGREED price whatever standard strategy is
// picked; the strategy prices the rest; a WIN-BACK strategy prices them all. Both the banner
// and the per-line marker read the lines' own rationale, so a saved quote shows it too.

function bookState(l) {
  const pb = (l.rationale || {}).price_book;
  if (!pb) return null;
  if ((l.rationale || {}).basis === 'price_book') return 'used';
  return pb.why_not === 'winback' ? 'winback' : 'suspect';
}

/** The price-book marker for one line's price cell ('' when the part is not in a book). */
export function bookPill(l) {
  const pb = (l.rationale || {}).price_book;
  const state = bookState(l);
  if (!state) return '';
  const where = `${pb.book_name} · break ${Number(pb.break_qty).toLocaleString('en-GB')}+`;
  if (state === 'used') {
    return `<div><span class="badge badge-green" title="${esc(`Their price book ${where} — the agreed price. The strategy does not move it.`)}">${getIcon('book-open', 12)} Price book</span></div>`;
  }
  if (state === 'winback') {
    return `<div><span class="badge badge-amber" title="${esc(`In their price book (${where}), but the win-back strategy priced it instead`)}">Book overridden · win-back</span></div>`;
  }
  return `<div><span class="badge badge-red" title="${esc(`${where}: the book price is 20× cost or more — looks like a unit error, so it was not used`)}">Book price not used</span></div>`;
}

/** An amber note when the price a rep TYPED differs from the line's agreed price-book price
 *  (compared at 4 dp — book prices are per-metre to four places). '' when nothing was typed,
 *  the line is not in a book, or the typed price matches. Never blocks saving: the rep may
 *  have a reason; the note makes sure they know they are moving an agreed price. */
export function bookOverrideNote(l, typed, ccy) {
  const pb = (l.rationale || {}).price_book;
  if (!pb || pb.price == null || typed == null || typed === '' || Number.isNaN(Number(typed))) return '';
  if (Math.round(Number(typed) * 10000) === Math.round(Number(pb.price) * 10000)) return '';
  return `<div style="margin-top:4px;font-size:var(--text-xs);color:var(--amber-700,#b45309);text-align:left;white-space:normal;max-width:220px">
    ${getIcon('alert-triangle', 12)} Differs from their agreed price-book price ${esc(money(ccy || 'GBP', 4)(pb.price))}
    (book ${esc(pb.book_name || '')}) — they agreed that price</div>`;
}

/** The banner: WHICH items are in their book and priced by it, and what the strategy did. */
export function bookBanner(lines, strategyName) {
  lines = lines || [];
  const used = lines.filter(l => bookState(l) === 'used');
  const over = lines.filter(l => bookState(l) === 'winback');
  const bad = lines.filter(l => bookState(l) === 'suspect');
  if (!used.length && !over.length && !bad.length) return '';
  const books = [...new Set([...used, ...over, ...bad].map(l => l.rationale.price_book.book_name))];
  const named = (ls) => ls.map(l => `<strong>${esc(l.sku)}</strong>`).join(', ');
  const others = lines.length - used.length;
  const strat = esc(strategyName || 'the strategy');
  const parts = [];
  if (used.length) {
    parts.push(`<div style="font-weight:600">${getIcon('book-open', 14)} ${used.length} of ${lines.length} item${lines.length === 1 ? '' : 's'} ${used.length === 1 ? 'is' : 'are'} in their price book
      “${esc(books.join('”, “'))}” and ${used.length === 1 ? 'is' : 'are'} priced at the agreed price</div>
      <div style="margin-top:4px">${named(used)}</div>
      <div class="sc-list-meta" style="margin-top:4px">${others
        ? `<strong>${strat}</strong> prices the other ${others} — choosing another strategy changes those, never these.`
        : `Every item is on the book, so the strategy changes nothing here.`}
        A win-back strategy is the one thing that prices book items differently.</div>`);
  }
  if (over.length) {
    parts.push(`<div style="${used.length ? 'margin-top:8px;' : ''}font-weight:600;color:var(--amber-700,#b45309)">${over.length} item${over.length === 1 ? ' is' : 's are'}
      in their price book but <em>${strat}</em> (a win-back) priced ${over.length === 1 ? 'it' : 'them'} instead</div>
      <div style="margin-top:4px">${named(over)}</div>
      <div class="sc-list-meta" style="margin-top:4px">Pick a standard strategy to quote the agreed book prices. ▸ on each line shows the book price beside the one used.</div>`);
  }
  if (bad.length) {
    parts.push(`<div style="margin-top:8px;color:var(--red-700,#b91c1c)"><strong>${bad.length} book price${bad.length === 1 ? '' : 's'} not used</strong> — 20× cost or more, which looks like a unit error in the book:
      ${named(bad)}. Check the book in pricing.</div>`);
  }
  const tone = used.length ? 'var(--green-600,#059669)' : 'var(--amber-500,#f59e0b)';
  return `<div class="card card-body" style="margin-bottom:var(--space-3);border-left:4px solid ${tone};font-size:var(--text-sm)">${parts.join('')}</div>`;
}

/** How one line's price was worked out, step by step, with every signal pricing looked at. */
export function workingsHtml(line, ccy) {
  const r = line.rationale || {};
  const s = r.signals || {};
  const m = fmtMoney(ccy);
  const row = (label, value, detail, on) => `<tr${on ? ' style="background:var(--brand-50,#e6f0f5)"' : ''}>
      <td style="white-space:nowrap">${label}${on ? ' <span class="badge badge-blue">used</span>' : ''}</td>
      <td style="text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap">${value}</td>
      <td class="sc-list-meta">${detail || ''}</td></tr>`;
  const pb = r.price_book;
  const signals = [
    pb ? row('Their price book', m(pb.price), [esc(pb.book_name), `break ${Number(pb.break_qty).toLocaleString('en-GB')}+`,
      pb.used ? 'agreed price' : pb.why_not === 'winback' ? 'not used — win-back' : 'not used — looks like a unit error']
      .join(' · '), r.basis === 'price_book') : '',
    row('They last paid', m(s.own_last), [s.own_last_date && shortDate(s.own_last_date), s.own_n && `${s.own_n} buys`,
      s.own_avg != null && `average ${m(s.own_avg)}`].filter(Boolean).join(' · '), r.basis === 'own_last'),
    row('We last quoted', m(s.quoted_last), [s.quoted_last_date && shortDate(s.quoted_last_date), s.quoted_n && `${s.quoted_n} quotes`]
      .filter(Boolean).join(' · '), r.basis === 'quoted_last'),
    s.prev_quote != null ? row('Their previous quote', m(s.prev_quote), '', r.basis === 'prev_quote') : '',
    row('Similar customers', m(s.cohort_median), s.cohort_n ? `median of ${s.cohort_n}${s.cohort_p25 != null ? ` · middle half ${m(s.cohort_p25)}–${m(s.cohort_p75)}` : ''}${
      s.cohort_low != null ? ` · range ${m(s.cohort_low)}–${m(s.cohort_high)}` : ''}${s.cohort_division ? ` · division ${esc(s.cohort_division)}` : ''}${
      s.cohort_comparable === false ? ' · not strictly comparable' : ''}` : 'no comparable customers', r.basis === 'cohort'),
    row('Cheapest competitor', m(s.vendor_cheapest), s.vendor_cheapest != null ? 'see competitors below' : 'no reading', r.basis === 'vendor_landscape'),
    'cost' in s || 'cost' in line ? row('Our cost', m(s.cost ?? line.cost), '', r.basis === 'cost_plus') : '',
  ].join('');
  const steps = [];
  if (r.basis === 'price_book') steps.push(`Their price book <strong>${esc(pb.book_name)}</strong> sets it: ${esc(m(pb.price))} at ${Number(pb.break_qty).toLocaleString('en-GB')}+ — an agreed price, so no strategy, surge or margin floor moves it`);
  else if (r.base_price != null) steps.push(`Starts from <strong>${esc(m(r.base_price))}</strong> — ${esc(BASIS[r.basis] || r.basis || 'no basis')}`);
  if (r.recency_uplift_years) steps.push(`uplifted for ${r.recency_uplift_years} year${r.recency_uplift_years === 1 ? '' : 's'} since that price`);
  if (r.strategy_mult != null && r.strategy_mult !== 1) steps.push(`× ${r.strategy_mult} for the ${esc(r.strategy || 'strategy')}`);
  if (r.condition_mult != null && r.condition_mult !== 1) steps.push(`× ${r.condition_mult} for this customer's profile`);
  if (r.condition_margin_add) steps.push(`margin floor ${r.condition_margin_add > 0 ? '+' : ''}${r.condition_margin_add} pts for this customer`);
  const flags = (r.flags || []).map(f => `<span class="badge badge-${(FLAG[f] || ['gray'])[0]}">${esc((FLAG[f] || [null, f.replace(/_/g, ' ')])[1])}</span>`).join(' ');
  const sg = r.surge;
  const surge = sg ? `<div style="margin-top:6px"><strong>Surge:</strong> ${sg.n_out} of ${sg.n_total} competitors out of stock${
    sg.max_lead ? ` (longest lead ${sg.max_lead} days)` : ''}${(sg.competitors_out || []).length ? ` — ${sg.competitors_out.map(esc).join(', ')}` : ''}${
    sg.our_stock != null ? ` · we hold ${Number(sg.our_stock).toLocaleString('en-GB')}` : ''} · ${sg.applied
      ? `<span class="badge badge-amber">+${sg.surge_pct}% applied</span>` : `+${sg.surge_pct}% available, not applied`}</div>` : '';
  const pv = r.previous;
  const prev = pv ? `<div style="margin-top:6px"><strong>Win-back:</strong> ${esc(pv.quote_no || 'previous quote')}${pv.date ? ` (${esc(shortDate(pv.date))})` : ''}
    was ${esc(m(pv.unit_price))} → now ${esc(m(line.recommended_price))}${pv.delta_pct != null ? ` (${pv.delta_pct > 0 ? '+' : ''}${pv.delta_pct}%)` : ''}${
      pv.above_previous ? ' <span class="badge badge-amber">above the previous quote</span>' : ''}</div>` : '';
  return `<div style="display:grid;grid-template-columns:minmax(280px,1fr) minmax(280px,1fr);gap:var(--space-4)">
    <div><div class="sc-list-meta" style="margin-bottom:4px">What pricing looked at</div>
      <table class="data-table sc-book-table"><tbody>${signals}</tbody></table></div>
    <div><div class="sc-list-meta" style="margin-bottom:4px">How the price was reached</div>
      ${steps.length ? `<ol style="margin:0;padding-left:1.2em;font-size:var(--text-sm)">${steps.map(x => `<li>${x}</li>`).join('')}
        <li>= <strong>${esc(m(line.recommended_price))}</strong>${line.margin_pct != null ? ` at ${line.margin_pct.toFixed ? line.margin_pct.toFixed(1) : line.margin_pct}% margin` : ''}</li></ol>`
        : '<p class="sc-list-meta">No evidence to price from.</p>'}
      ${flags ? `<div style="margin-top:6px">${flags}</div>` : ''}${surge}${prev}
    </div></div>
    <div data-competitors style="margin-top:var(--space-3)"></div>`;
}

/** Competitor price, stock and lead time for one part — pricing's /market, on demand. */
export async function loadCompetitors(box, entity, part, customer) {
  if (!box || box.dataset.loaded) return;
  box.dataset.loaded = '1';
  box.innerHTML = '<p class="sc-list-meta">Loading competitors…</p>';
  let d;
  try { d = (await salesApi.getPartMarket(entity, part, customer)).data || {}; } catch (e) {
    box.innerHTML = `<p class="sc-list-meta">Competitors could not be checked (${esc(e.message || 'no answer')}).</p>`;
    return;
  }
  const mk = d.market;
  const vendors = (mk && mk.vendors) || [];
  if (!mk || !vendors.length) {
    box.innerHTML = '<p class="sc-list-meta">No competitor readings for this part.</p>';
    return;
  }
  const q = (v) => Number(v).toLocaleString('en-GB');
  box.innerHTML = `<div class="sc-list-meta" style="margin-bottom:4px">Competitors (pricing's readings, per our ${esc(mk.unit || 'unit')})</div>
    <table class="data-table sc-book-table"><thead><tr><th>Vendor</th><th>Stock</th><th style="text-align:right">Lead</th>
      <th style="text-align:right">Price</th><th style="text-align:right">From qty</th></tr></thead><tbody>${vendors.map(x => `<tr${x.fresh ? '' : ' style="opacity:.55"'}>
      <td>${esc(x.name)}${(x.cheapest_at || []).length ? ' <span class="badge badge-green">cheapest</span>' : ''}</td>
      <td>${x.in_stock == null ? '<span class="sc-list-meta">not shown</span>' : x.in_stock ? `<span class="badge badge-green">in stock</span> ${x.stock_qty != null ? q(x.stock_qty) : ''}` : '<span class="badge badge-gray">none</span>'}</td>
      <td style="text-align:right">${x.lead_time_days != null ? `${x.lead_time_days} d` : '—'}</td>
      <td style="text-align:right;font-variant-numeric:tabular-nums">${x.unit_price_at_first_break == null ? '—'
        : `${Number(x.unit_price_at_first_break).toLocaleString('en-GB', { maximumFractionDigits: 4 })} ${esc(x.currency || '')}`}</td>
      <td style="text-align:right">${x.first_break != null ? q(x.first_break) : '—'}</td></tr>`).join('')}</tbody></table>`;
}
